import { WebSectionTrackingData } from '@ahoy/types';
import { GtmService } from '@APP/services/gtm-service';
import { LayoutBlock } from '@FLAVOR/../../common/types';
import { css, html, nothing, PropertyValues } from 'lit';
import { customElement, property, query, state } from 'lit/decorators';
import { classMap } from 'lit/directives/class-map';
import { Events } from '../../events/app-events';
import { DepInjMixin, requires } from '../../mixins/dependency';
import { ConfigService } from '../../services/config-service';
import { QuestionsService } from '../../services/questions-service';
import { UserService } from '../../services/user-service';
import { eventBus } from '../../utils/event-bus';
import { DeviceAwareComponent } from './device-aware-component';
import { LHBlock } from './lh-block';
import { TextParamsForLandingPage } from '@ahoy/types/questionnaire';
import { t } from '@APP/utils/locale';

type Deps = {
  questionsService: QuestionsService;
  configService: ConfigService;
  userService: UserService;
  gtmService: GtmService;
};

@requires(QuestionsService.serviceName, ConfigService.serviceName, UserService.serviceName, GtmService.serviceName)
@customElement('lh-questionnaire')
export class LHQuestionnaire extends DepInjMixin<Deps>(DeviceAwareComponent) {
  static get baseStyles() {
    return [
      css`
        :host {
          display: flex;
          flex-direction: column;
          box-sizing: border-box;
        position: relative;
        overflow-y: auto;
        overflow-x: hidden;
      }
      lh-block {
        transform: translate3d(0, 0, 0);
        position: absolute;
        transition: transform 350ms var(--transition-page);
        transition-delay: 0ms;
      }
      lh-block.is-behind.first-block-on-back {
        transition: none;
      }
      lh-block[active] {
        transition: transform 650ms var(--transition-page);
      }
      :host(.in-transition) #blocks-wrapper {
        transform: translate3d(0, -100vh, 0);
        transition: transform 250ms var(--transition-page);
      }
      #blocks-wrapper {
        transition: transform 650ms var(--transition-page);
        transition-delay: 250ms;
        transform: translate3d(0, 0, 0);
        position: relative;
        flex-grow: 1;
        overflow-x: hidden;
        width: 100%;
      }
      :host([data-device='desktop']) #sequence-progress {
        position: absolute;
        top: 0;
        right: 1rem;
      }
      #sequence-progress {
        display: flex;
        text-align: center;
        height: 6rem;
        font-size: 1.7rem;
        color: var(--c-text-understate);
        font-weight: 400;
        justify-content: center;
        align-items: center;
        unicode-bidi: initial;
      }
      :host([current-section='S_Summary']) #sequence-progress {
        display: none;
      }
      :host([current-section='S_Boat']) {
        --grid-spacing: repeat(8, 18rem);
      }

      @supports (-webkit-touch-callout: none) {
        /* CSS specific to iOS devices */
        .is-ahead {
          display: none !important;
          transform: none !important;
        }
        .is-behind {
          display: none !important;
          transform: none !important;
        }
      }
      :host::-webkit-scrollbar {
        width: 1rem;
      }
      :host::-webkit-scrollbar-track {
        border-radius: 16px;
        background: rgba(193, 193, 193, 0.52);
      }
      :host::-webkit-scrollbar-thumb {
        border-radius: 16px;
        background: #b8c7d9;
      }
    `,
    ];
  };
  static get desktopStyles() {
    return [
      css`
        lh-block {
          padding: var(--lh-block-padding, 7.25rem 9rem 3rem 10rem);
          display: flex;
        inset-inline-start: 0;
        inset-block-start: 0;
      }
      *::-webkit-scrollbar {
        width: 1rem;
      }
      *::-webkit-scrollbar-track {
        border-radius: 16px;
        background: transparent;
      }
      *::-webkit-scrollbar-thumb {
        border-radius: 16px;
        background: rgba(193, 193, 193, 0.52);
      }
       #blocks-wrapper.overflow-x {
        overflow-x: auto;
      }

    `,
    ];
  };
  static get mobileStyles() {
    return [
      css`
      #blocks-wrapper {
        width: 100vw;
      }
      #sequence-progress {
        height: auto;
        padding: 2rem;
        text-align: left;
        justify-content: start;
            padding-inline-start: 3.75rem;
      }
  `];
  };
  static get rtlStyles() {
    return [
      css`
      .is-ahead {
        transform: translate3d(-100vw, 0, 0);
        pointer-events: none;
      }
      .is-behind {
        transform: translate3d(100vw, 0, 0);
        pointer-events: none;
      }
      .left_corner {
          position: absolute;
          background-image: url(./assets/Intersect_left_top.svg);
          background-position: right top;
          background-repeat: no-repeat;
          transform: rotate(180);
          width: 55px;
          height: 62px;
      }
    `,
    ];
  };
  static get ltrStyles() {
    return [
      css`
        .is-ahead {
          transform: translate3d(100vw, 0, 0);
          pointer-events: none;
      }
      .is-behind {
        transform: translate3d(-100vw, 0, 0);
        pointer-events: none;
      }
      .left_corner {
          position: absolute;
          background-image: url(./assets/Intersect_left_top.svg);
          background-position: left top;
          background-repeat: no-repeat;
          width: 55px;
          height: 62px;
      }
    `,
    ];
  };

  private _loadLineupInflight: Promise<void> | null = null;

  @property({ attribute: 'current-section', reflect: true })
    currentSectionName: string = '';

  @state()
    isFirstSequence = true;

  @state()
    lineup!: { questionIds: string[]; }[];

  @property({ reflect: true })
    currentBlockIdx = -1;


  @property({ type: Boolean, reflect: true })
    isNewLandingPage = false;
  private subs: Function[] = [];

  private firstTime: boolean = true;

  @query('lh-block[active]')
    currentBlockElement: LHBlock | undefined;

  connectedCallback() {
    super.connectedCallback();
    this.currentBlockIdx = 0;
    this.subs.push(
      eventBus().on(Events.Type.SectionChanged, (s) => {
        this.currentBlockIdx = this.deps.questionsService.model.getBlockToStart(s.sectionId, s.goingBack);
        this.animateOut().then(async() => {
          await this.loadLineup();
          this.animateIn();
        });
      }),
      eventBus().on(Events.Type.Refresh, () => {
        requestAnimationFrame(async() => {
          await this.loadLineup();
          // this.setFirstEmptyBlock();
        });
        const deviceTypeQuestion = this.deps.questionsService.model.getQuestionById('device_type');
        if (deviceTypeQuestion) {
          deviceTypeQuestion.value = this.userDevice === 'mobile' ? 'Mobile' : 'Desktop';
        }
      }),
      eventBus().on(Events.Type.LoginSuccess, () => {
        if (this.deps.userService.userType == 'customer') {
          this.deps.questionsService.welcomePopup();
        } else {
          eventBus().emit(Events.Type.Refresh, this.deps.questionsService.model.getStatus());
        }
      }),
      eventBus().on(Events.Type.ErrorPopupClosed, (section) => {
        if (['S_Payment'].includes(section)) {
          return;
        } else {
          window.history.pushState({}, '');
          this.requestPrevBlock();
        }
      }),
      eventBus().on(Events.Type.ChangePhoneNumber, async() => {
        this.requestPrevBlock();
        setTimeout(() => eventBus().emit(Events.Type.FocusOnPhoneElement, 1000));
      }),
      // eventBus().on(Events.Type.RequestFirstBlock, () => {
      //   this.deps.questionsService.model.setActiveSection(this.deps.questionsService.model.getFirstSection());
      //   this.setFirstEmptyBlock();
      // }),
      eventBus().on(Events.Type.Back, () => {
        if (this.isFirstSequence && this.currentBlockIdx == 0) {
          eventBus().emit(Events.Type.Logout, { reload: false, origin: 'Back button' });
        } else {
          window.history.pushState({}, '');
          this.requestPrevBlock();
        }
      }),
    );
    requestAnimationFrame(async() => await this.loadLineup());
    const loggedIn = this.deps.userService.loginMode == 'login';
    const cutomer = this.deps.userService.userType == 'customer';
    const newQuestionnaire = this.deps.questionsService.model.getStatus() == 'Mem';
    const conditionsForWelcomePopup = cutomer && loggedIn && newQuestionnaire;
    if (conditionsForWelcomePopup) {
      this.deps.questionsService.welcomePopup();
    }
  }

  updated(props: PropertyValues<this>) {
    if (props.has('currentBlockIdx')) {
      const { model } = this.deps.questionsService;
      const sectionName = model.getActiveSectionName()!;
      this.isFirstSequence = !model.getSectionBefore(sectionName);
      this.currentSectionName = sectionName;

      requestAnimationFrame(() => {
        try {
          this.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
          this.currentBlockElement?.scrollIntoView();
        } catch  {
          this.scrollTo(0, 0);
          this.currentBlockElement?.scrollIntoView();
        }
      });
    }
    requestAnimationFrame(() => {
      // @ts-ignore
      (this.shadowRoot?.querySelector('#blocks-wrapper') || {}).scrollLeft = 0;
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.subs.forEach(f => f());
  }

  animateOut() {
    const onDesktop = this.userDevice === 'desktop';
    const timeOut = onDesktop ? 100 : 450;
    if (!onDesktop) {
      this.classList.add('in-transition');
    }
    return new Promise((resolve) => setTimeout(resolve, timeOut));
  }

  animateIn() {
    if (this.userDevice != 'desktop') {
      this.dispatchEvent(new Event('animate-in'));
      this.classList.remove('in-transition');
    }
  }
  // async loadLineup() {
  //   const { model, questionnaireId } = this.deps.questionsService;
  //   if (this.firstTime && questionnaireId !== undefined) {
  //     await this.setFirstEmptyBlock();
  //   }

  //   const sectionName = model.getActiveSectionName() || 'S_Welcome';
  //   this.isFirstSequence = !model.getSectionBefore(sectionName);
  //   this.lineup = model.getBlocksBySection(sectionName);
  //   this.currentSectionName = sectionName;
  //   requestAnimationFrame(() => {
  //     if (this.currentBlockIdx >= this.lineup.length) {
  //       this.currentBlockIdx = 0;
  //     }
  //     this.currentBlockElement?.scrollIntoView();
  //   });
  // }

  // this was added to prevent multiple parallel calls to loadLineup
  async loadLineup() {
    if (this._loadLineupInflight) return this._loadLineupInflight;
    this._loadLineupInflight = this._loadLineupInternal().finally(() => {
      this._loadLineupInflight = null;
    });
    return this._loadLineupInflight;
  }

  private async _loadLineupInternal() {
    const { model, questionnaireId } = this.deps.questionsService;
    if (this.firstTime && questionnaireId !== undefined) {
      await this.setFirstEmptyBlock();
    }
    const sectionName = model.getActiveSectionName() || 'S_Welcome';
    this.isFirstSequence = !model.getSectionBefore(sectionName);
    this.lineup = model.getBlocksBySection(sectionName);
    this.currentSectionName = sectionName;

    requestAnimationFrame(() => {
      if (this.currentBlockIdx >= this.lineup.length) this.currentBlockIdx = 0;
      this.currentBlockElement?.scrollIntoView();
    });
  }

  hasPresentableQuestions() {
    const { currentBlockIdx, lineup } = this;
    if (lineup && lineup[currentBlockIdx]) {
      const questions = (lineup[currentBlockIdx].questionIds || []).map((id) =>
        this.deps.questionsService.model.getQuestionById(id),
      );
      return !questions.every((q) => q && q.hide);
    }
    return true;
  }

  async setFirstEmptyBlock() {
    const { model } = this.deps.questionsService;
    if (model.getActiveSectionName() === 'S_Summary') return;

    if (model.getStatus() == 'Mem') {
      model.setActiveSection('S_Welcome');
    } else {

      const last_Section = model.getLeftSection();
      let current_section = 'S_Welcome';
      let valid = true;
      const isOcr = this.deps.questionsService.isOcr;
      while (current_section && current_section != last_Section && valid) {
        let result = await this.deps.questionsService.checkIfValid(current_section);
        const isStormStateErr = this.deps.questionsService.checkIfStateIsInStormStates();
        const isSameDayBindErr = result.errorType === 'SameDayBind';
        const isPreviousDayBindErr = result.errorType === 'PreviusDateBind';
        const isNoDrivingLicenseErr = result.errorType === 'UW_Error_20';
        const isNoYachtProgramErr = result.errorType && ['UW_Error_21D','UW_Error_23','UW_Error_24','UW_Error_24B','UW_Error_24C','Driver-Error'].includes(result.errorType);
        if (!result.value && !isPreviousDayBindErr && !isStormStateErr && !isSameDayBindErr && !isNoDrivingLicenseErr && !isNoYachtProgramErr) {
          //new yacht program check
          const newYachtProgramError = await this.deps.questionsService.checkIfValidForNewYachtProgram({ currentSection: current_section, originalErrCode: result.errorType });
          if (!newYachtProgramError.value) {
            result = newYachtProgramError;
          }
        }
        valid = result.value;
        if (valid) {
          current_section = model.getSectionAfter(current_section) || '';
        } else {
          this.deps.questionsService.showErrorsAsPopup(result.msg);
        }
      }
      if (current_section == 'S_Welcome' || !current_section) {
        const isMobile = this.userDevice === 'mobile';
        current_section = isOcr && !isMobile? 'S_Boat_OCR' : 'S_Boat';
      }
      model.setActiveSection(current_section);
    }
    this.currentBlockIdx = 0;
    const serverErrorQuestion = this.deps.questionsService.model.getQuestionById('Server_Error');
    let serverError = serverErrorQuestion?.value as string;
    // for agents we remove the server error message if it is "Please enter your initials:: This field is required"
    const isAgent = this.deps.questionsService.isAgent();
    const isExternalAgent = this.deps.questionsService.isExternalAgent();
    const isBoatInitialsServerError = serverError === 'Please enter your initials:: This field is required';
    const isPaymentLinkMode = this.deps.questionsService.getPaymentLinkMode();
    if ((isAgent || isExternalAgent || isPaymentLinkMode) && serverErrorQuestion && isBoatInitialsServerError) {
      serverErrorQuestion.value = '';
      serverError = '';
    }
    if (serverError) {
      // initially this was used to render the full text of the error message by the errorCode stored before the
      // | delimiter. Now this is just a mechanism to skip showing the Server_Error if it contains a | delimiter.
      // NOT USED if the value contains the | delimiter we split it and get the first value. 
      // NOT USED if it equals overrideValue we get the question by id of the first value, and look for the
      // NOT USED prop errorDescCode in its metadata. We then set the value of serverError to the value of errorDescCode.
      if (serverError.includes('|')) {
        const splitError = serverError.split('|');
        if (splitError[0] === 'overrideValue') {
          const question = this.deps.questionsService.model.getQuestionById(splitError[0]);
          if (question && question?.metadata?.errorDescCode) {
            const supportNumber = this.deps.questionsService.model.getSupportPhoneNumberText();
            serverError = t(question?.metadata?.errorDescCode, supportNumber) || serverError;
          }
        }
      } else {
        this.deps.questionsService.showErrorsAsPopup(serverError);
      }
    }

    if (this.userDevice != 'desktop') {
      this.currentBlockElement?.scrollIntoView();
    }
    this.firstTime = false;
  }

  getBlockLastQuestion() {
    const { model } = this.deps.questionsService;
    const questionIds = this.lineup[this.currentBlockIdx].questionIds;
    return model.getQuestionById(questionIds[questionIds.length - 1]);
  }

  async requestNextBlock() {
    const { model } = this.deps.questionsService;

    if (model.getStatus() == 'Mem' && this.deps.userService.isLoggedIn && this.deps.userService.completedWelcomeSection) {
      this.deps.userService.getQuestionaire({ createIfNotExist: true, isMobile: this.userDevice === 'mobile' });
      return;
    }

    //reset fromCheckout in back from billing
    this.deps.questionsService.isFromCCpayment = false;

    const lineupLength = (this.lineup || []).length;
    if (this.currentBlockIdx < lineupLength - 1) {
      /** running zip validations */
      let blockValid;
      if (this.userDevice === 'mobile' && ['S_Address'].includes(model.getActiveSectionName() || '')) {
        blockValid = await this.deps.questionsService.validateAutocompleteAddress(this.currentBlockIdx);
      } else blockValid = true;
      if (blockValid) this.currentBlockIdx++;
      if (!this.hasPresentableQuestions()) {
        this.requestNextBlock();
      }
    } else {
      const sectionName = model.getActiveSectionName()!;
      const nextSection = model.getSectionAfter(sectionName);
      this.setSection(nextSection || '');
    }
  }

  setSection(section: string) {
    if (section) {
      window.blur();
      this.deps.questionsService
        .requestSectionChange(section)
        .then(
          // success
          () => {
            this.addEventListener(
              'animate-in',
              () => (this.currentBlockIdx = 0),
              {
                once: true,
              },
            );
          },
        );
    }
  }

  requestPrevBlock() {
    if (this.currentBlockIdx > 0) {
      this.currentBlockIdx--;
      if (!this.hasPresentableQuestions()) {
        this.requestPrevBlock();
      }
    } else {
      const { model } = this.deps.questionsService;
      const sectionName = model.getActiveSectionName()!;
      let prevSection = model.getSectionBefore(sectionName);

      //reset fromCheckout in back from billing
      this.deps.questionsService.isFromCCpayment = false;

      if (prevSection) {
        const data: WebSectionTrackingData = {
          section: sectionName,
          next_section: prevSection,
          operation: 'prev',
          errorMessage: '',
        };
        eventBus().emit(Events.Type.SaveWebSectionTracking, data);

        model.setActiveSectionOnBackButton(prevSection);
      }
    }
  }

  getLineupForDesktop(): LayoutBlock[] {
    const sectionId = this.deps.questionsService.model.getActiveSectionName();
    const parts = this.deps.questionsService.model.getLayoutGrouping(sectionId);
    return parts;
  }

  renderHeaderMTA() {
    const headerHTML = html`<lh-header-mta></lh-header-mta>`;
    return headerHTML;
  }

  renderHeaderGetAssistance() {
    const headerHTML = html`<lh-header-get-assistance></lh-header-get-assistance>`;
    return headerHTML;
  }

  private renderCustomLandingPage({ textParamsForLandingPage }:{textParamsForLandingPage: TextParamsForLandingPage}) {
    return html`<lh-custom-landing-page @request-next-block=${this.requestNextBlock} .textParamsForLandingPage=${textParamsForLandingPage}></lh-custom-landing-page>`;
  }

  renderMobileLayout({ currentBlockIdx, layout, isDesktopMode }: any) {
    const progressText = `${currentBlockIdx + 1} / ${this.lineup.length}`;
    const divMobileClasses = {
      'overflow-x': false,
      'left_corner': layout != 'content' && this.userDevice != 'mobile',
    };

    return html`
        ${this.lineup.length > 1 ? html`<span dir="ltr" id="sequence-progress">${progressText}</span>` : ''}
        <div id="blocks-wrapper" class=${classMap(divMobileClasses)}>
          ${(this.lineup || []).map((block, idx) => {
    const classList = {
      active: currentBlockIdx === idx,
      'is-ahead': idx > currentBlockIdx,
      'is-behind': idx < currentBlockIdx,
      'hide-back-button': this.isFirstSequence && idx === 0,
    };
    return html`
              <lh-block
                ?desktop-mode=${isDesktopMode}
                .blockIndex=${idx}
                ?active=${currentBlockIdx === idx}
                @request-next-block=${this.requestNextBlock}
                @request-prev-block=${this.requestPrevBlock}
                class="${classMap(classList)}"
                .questions=${block.questionIds}>
              </lh-block>
            `;
  })}
        </div>
      `;
  }
  renderDesktopLayout({ currentBlockIdx, layout, isDesktopMode, currentSection }: any) {
    const { questionsService } = this.deps;
    const isMTA = questionsService.model.getQuestionById('Is_MTA')?.value == '$_BOOL_true' || questionsService.model.getQuestionById('Is_Renewal')?.value == '$_BOOL_true';
    const lineup = this.getLineupForDesktop();
    const renderMtaHeaderConditions = questionsService.model.isTestApp('show-mta') || (isMTA && !['S_Summary_Details', 'S_MTA_Coverages'].includes(currentSection));
    const renderGetAssistanceConditions = !questionsService.isAgentOrExternalAgent() && !isMTA && ['S_Pricing'].includes(currentSection);
    const progressText = '';
    const divDesktopClasses = {
      'overflow-x': ['S_Pricing'].includes(currentSection),
      'left_corner': layout != 'content' && this.userDevice != 'mobile',
    };

    return html`
        <span dir="ltr" id="sequence-progress">${progressText}</span>
        <div id="blocks-wrapper" class=${classMap(divDesktopClasses)}>
          ${renderMtaHeaderConditions ? this.renderHeaderMTA() : nothing}
          ${renderGetAssistanceConditions ? this.renderHeaderGetAssistance() : nothing}
          ${(lineup || []).map((block, idx) => {
    const classList = {
      active: currentBlockIdx === idx,
      'is-ahead': idx > currentBlockIdx,
      'is-behind': idx < currentBlockIdx,
      'first-block-on-back': currentSection === 'S_Boat_Garage',
      'hide-back-button': this.isFirstSequence && idx === 0,
    };
    return html`
              <lh-block
                ?desktop-mode=${isDesktopMode}
                .blockIndex=${idx}
                ?active=${currentBlockIdx === idx}
                @request-next-block=${this.requestNextBlock}
                @request-prev-block=${this.requestPrevBlock}
                class="${classMap(classList)}"
                .questions=${block[0].questions[0]}
              >
              </lh-block>
            `;
  })}
        </div>
      `;
  }

  renderContent() {
    const { currentBlockIdx } = this;
    const { model } = this.deps.questionsService;
    const isDesktopMode = this.userDevice === 'desktop';
    const layout = model.getCurrentSectionLayout();
    const lineupLength = isDesktopMode ? this.getLineupForDesktop().length : (this.lineup || []).length;
    const currentSection = model.getActiveSectionName() || '';

    const conditionsForCustomLandingPage = currentSection === 'S_Welcome' && currentBlockIdx === 0 && this.isNewLandingPage;
    const textParamsForLandingPage = this.deps.userService.getTextParamsForLandingPage();
    const conditionsForMobile = !isDesktopMode && this.lineup && this.lineup.length;
    const conditionsForDesktop = isDesktopMode && currentBlockIdx + 1 <= lineupLength;

    if (conditionsForCustomLandingPage) { return this.renderCustomLandingPage({ textParamsForLandingPage }); }
    if (conditionsForMobile) { return this.renderMobileLayout({ currentBlockIdx, layout, isDesktopMode }); }
    if (conditionsForDesktop) { return this.renderDesktopLayout({ currentBlockIdx, layout, isDesktopMode, currentSection }); }
    return html``;
  }
}